import * as THREE from 'three';
import proj4 from 'proj4';
import * as TWEEN from '@tweenjs/tween.js';
import { tweenGroup } from '../viewer-utils/tween-group.js';

export class Utils {
  static computeTransformedBoundingBox(box: THREE.Box3, transform: THREE.Matrix4): THREE.Box3 {
    const vertices = [
      new THREE.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.min.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.max.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.max.y, box.min.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.min.y, box.max.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.min.y, box.max.z).applyMatrix4(transform),
      new THREE.Vector3(box.min.x, box.max.y, box.max.z).applyMatrix4(transform),
      new THREE.Vector3(box.max.x, box.max.y, box.max.z).applyMatrix4(transform),
    ];

    const boundingBox = new THREE.Box3();
    boundingBox.setFromPoints(vertices);

    return boundingBox;
  }

  static addCommas(nStr: string | number): string {
    const s = String(nStr);
    const x = s.split('.');
    let x1 = x[0];
    const x2 = x.length > 1 ? '.' + x[1] : '';
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1' + ',' + '$2');
    }
    return x1 + x2;
  }

  static moveTo(scene: any, endPosition: THREE.Vector3, endTarget: THREE.Vector3): void {
    const view = scene.view;
    const camera = scene.getActiveCamera();
    const animationDuration = 500;
    const easing = TWEEN.Easing.Quartic.Out;

    {
      const tween = new TWEEN.Tween(view.position, tweenGroup).to(endPosition, animationDuration);
      tween.easing(easing);
      tween.start();
    }

    {
      const camTargetDistance = camera.position.distanceTo(endTarget);
      const target = new THREE.Vector3().addVectors(
        camera.position,
        camera.getWorldDirection(new THREE.Vector3()).clone().multiplyScalar(camTargetDistance),
      );
      const tween = new TWEEN.Tween(target, tweenGroup).to(endTarget, animationDuration);
      tween.easing(easing);
      tween.onUpdate(() => {
        view.lookAt(target);
      });
      tween.onComplete(() => {
        view.lookAt(target);
      });
      tween.start();
    }
  }

  static loadSkybox(path: string): {
    camera: THREE.PerspectiveCamera;
    scene: THREE.Scene;
    parent: THREE.Object3D;
  } {
    const parent = new THREE.Object3D();

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      1,
      100000,
    );
    camera.up.set(0, 0, 1);
    const scene = new THREE.Scene();

    const format = '.jpg';
    const urls = [
      path + 'px' + format,
      path + 'nx' + format,
      path + 'py' + format,
      path + 'ny' + format,
      path + 'pz' + format,
      path + 'nz' + format,
    ];

    const materialArray: THREE.MeshBasicMaterial[] = [];
    {
      for (let i = 0; i < 6; i++) {
        const material = new THREE.MeshBasicMaterial({
          map: null,
          side: THREE.BackSide,
          depthTest: false,
          depthWrite: false,
          color: 0x424556,
        });

        materialArray.push(material);

        const loader = new THREE.TextureLoader();
        loader.load(
          urls[i],
          function loaded(texture) {
            material.map = texture;
            material.needsUpdate = true;
            material.color.setHex(0xffffff);
          },
          function progress(_xhr) {
            // loading progress
          },
          function error(xhr) {
            console.error('An error happened', xhr);
          },
        );
      }
    }

    const skyGeometry = new THREE.BoxGeometry(700, 700, 700);
    const skybox = new THREE.Mesh(skyGeometry, materialArray);

    scene.add(skybox);

    scene.traverse(n => (n.frustumCulled = false));

    // z up
    scene.rotation.x = Math.PI / 2;

    parent.children.push(camera);
    camera.parent = parent;

    return { camera, scene, parent };
  }

  static createBackgroundTexture(width: number, height: number): THREE.DataTexture {
    function gauss(x: number, y: number): number {
      return (1 / (2 * Math.PI)) * Math.exp(-(x * x + y * y) / 2);
    }

    const size = width * height;
    const data = new Uint8Array(4 * size);

    const chroma = [1, 1.5, 1.7];
    const max = gauss(0, 0);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const u = 2 * (x / width) - 1;
        const v = 2 * (y / height) - 1;

        const i = x + width * y;
        const d = gauss(2 * u, 2 * v) / max;
        let r = (Math.random() + Math.random() + Math.random()) / 3;
        r = (d * 0.5 + 0.5) * r * 0.03;
        r = r * 0.4;

        data[4 * i + 0] = 255 * (d / 15 + 0.05 + r) * chroma[0];
        data[4 * i + 1] = 255 * (d / 15 + 0.05 + r) * chroma[1];
        data[4 * i + 2] = 255 * (d / 15 + 0.05 + r) * chroma[2];
        data[4 * i + 3] = 255;
      }
    }

    const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
    texture.needsUpdate = true;

    return texture;
  }

  static getMousePointCloudIntersection(
    mouse: { x: number; y: number },
    camera: THREE.Camera,
    viewer: any,
    pointclouds: any[],
    params: { pickClipped?: boolean } = {},
  ): { location: THREE.Vector3; distance: number; pointcloud: any; point: any } | null {
    const renderer = viewer.renderer;

    const nmouse = {
      x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
      y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1,
    };

    const pickParams: any = {};

    if (params.pickClipped) {
      pickParams.pickClipped = params.pickClipped;
    }

    pickParams.x = mouse.x;
    pickParams.y = renderer.domElement.clientHeight - mouse.y;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(nmouse.x, nmouse.y), camera);
    const ray = raycaster.ray;

    let selectedPointcloud: any = null;
    let closestDistance = Infinity;
    let closestIntersection: THREE.Vector3 | null = null;
    let closestPoint: any = null;

    for (const pointcloud of pointclouds) {
      const point = pointcloud.pick(viewer, camera, ray, pickParams);

      if (!point) {
        continue;
      }

      const distance = camera.position.distanceTo(point.position);

      if (distance < closestDistance) {
        closestDistance = distance;
        selectedPointcloud = pointcloud;
        closestIntersection = point.position;
        closestPoint = point;
      }
    }

    if (selectedPointcloud) {
      return {
        location: closestIntersection!,
        distance: closestDistance,
        pointcloud: selectedPointcloud,
        point: closestPoint,
      };
    } else {
      return null;
    }
  }

  static getMouseTerrainIntersection(
    mouse: { x: number; y: number },
    camera: THREE.Camera,
    viewer: any,
  ): { location: THREE.Vector3; distance: number } | null {
    const nmouse = {
      x: (mouse.x / viewer.renderer.domElement.clientWidth) * 2 - 1,
      y: -(mouse.y / viewer.renderer.domElement.clientHeight) * 2 + 1,
    };

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(nmouse.x, nmouse.y), camera);

    const terrainObjects: THREE.Object3D[] = [];

    if (viewer.tilesManager && viewer.tilesManager.enabled && viewer.tilesManager.tilesGroup) {
      terrainObjects.push(viewer.tilesManager.tilesGroup);
    }

    if (
      viewer.quantizedTerrainManager &&
      viewer.quantizedTerrainManager.enabled &&
      viewer.quantizedTerrainManager.terrainGroup
    ) {
      terrainObjects.push(viewer.quantizedTerrainManager.terrainGroup);
    }

    if (terrainObjects.length === 0) {
      return null;
    }

    let closestIntersection: THREE.Intersection | null = null;

    for (const group of terrainObjects) {
      const intersections = raycaster.intersectObject(group, true);
      if (intersections.length > 0) {
        if (!closestIntersection || intersections[0].distance < closestIntersection.distance) {
          closestIntersection = intersections[0];
        }
      }
    }

    if (closestIntersection) {
      return {
        location: closestIntersection.point,
        distance: closestIntersection.distance,
      };
    }

    return null;
  }

  static mouseToRay(
    mouse: { x: number; y: number },
    camera: THREE.Camera,
    width: number,
    height: number,
  ): THREE.Ray {
    const normalizedMouse = {
      x: (mouse.x / width) * 2 - 1,
      y: -(mouse.y / height) * 2 + 1,
    };

    const vector = new THREE.Vector3(normalizedMouse.x, normalizedMouse.y, 0.5);
    const origin = camera.position.clone();
    vector.unproject(camera);
    const direction = new THREE.Vector3().subVectors(vector, origin).normalize();

    return new THREE.Ray(origin, direction);
  }

  static projectedRadius(
    radius: number,
    camera: THREE.Camera,
    distance: number,
    screenWidth: number,
    screenHeight: number,
  ): number {
    if (camera instanceof THREE.OrthographicCamera) {
      return Utils.projectedRadiusOrtho(radius, camera.projectionMatrix, screenWidth, screenHeight);
    } else if (camera instanceof THREE.PerspectiveCamera) {
      return Utils.projectedRadiusPerspective(
        radius,
        (camera.fov * Math.PI) / 180,
        distance,
        screenHeight,
      );
    } else {
      throw new Error('invalid parameters');
    }
  }

  static projectedRadiusPerspective(
    radius: number,
    fov: number,
    distance: number,
    screenHeight: number,
  ): number {
    let projFactor = 1 / Math.tan(fov / 2) / distance;
    projFactor = (projFactor * screenHeight) / 2;

    return radius * projFactor;
  }

  static projectedRadiusOrtho(
    radius: number,
    proj: THREE.Matrix4,
    screenWidth: number,
    screenHeight: number,
  ): number {
    let p1: any = new THREE.Vector4(0);
    let p2: any = new THREE.Vector4(radius);

    p1.applyMatrix4(proj);
    p2.applyMatrix4(proj);
    p1 = new THREE.Vector3(p1.x, p1.y, p1.z);
    p2 = new THREE.Vector3(p2.x, p2.y, p2.z);
    p1.x = (p1.x + 1.0) * 0.5 * screenWidth;
    p1.y = (p1.y + 1.0) * 0.5 * screenHeight;
    p2.x = (p2.x + 1.0) * 0.5 * screenWidth;
    p2.y = (p2.y + 1.0) * 0.5 * screenHeight;
    return p1.distanceTo(p2);
  }

  static generateDataTexture(width: number, height: number, color: THREE.Color): THREE.DataTexture {
    const size = width * height;
    const data = new Uint8Array(4 * width * height);

    const r = Math.floor(color.r * 255);
    const g = Math.floor(color.g * 255);
    const b = Math.floor(color.b * 255);

    for (let i = 0; i < size; i++) {
      data[i * 3] = r;
      data[i * 3 + 1] = g;
      data[i * 3 + 2] = b;
    }

    const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
    texture.needsUpdate = true;
    texture.magFilter = THREE.NearestFilter;

    return texture;
  }

  static createChildAABB(aabb: THREE.Box3, index: number): THREE.Box3 {
    const min = aabb.min.clone();
    const max = aabb.max.clone();
    const size = new THREE.Vector3().subVectors(max, min);

    if ((index & 0b0001) > 0) {
      min.z += size.z / 2;
    } else {
      max.z -= size.z / 2;
    }

    if ((index & 0b0010) > 0) {
      min.y += size.y / 2;
    } else {
      max.y -= size.y / 2;
    }

    if ((index & 0b0100) > 0) {
      min.x += size.x / 2;
    } else {
      max.x -= size.x / 2;
    }

    return new THREE.Box3(min, max);
  }

  static lineToLineIntersection(
    P0: THREE.Vector3,
    P1: THREE.Vector3,
    P2: THREE.Vector3,
    P3: THREE.Vector3,
  ): THREE.Vector3 {
    const P = [P0, P1, P2, P3];

    const d = (m: number, n: number, o: number, p: number): number => {
      return (
        (P[m].x - P[n].x) * (P[o].x - P[p].x) +
        (P[m].y - P[n].y) * (P[o].y - P[p].y) +
        (P[m].z - P[n].z) * (P[o].z - P[p].z)
      );
    };

    const mua =
      (d(0, 2, 3, 2) * d(3, 2, 1, 0) - d(0, 2, 1, 0) * d(3, 2, 3, 2)) /
      (d(1, 0, 1, 0) * d(3, 2, 3, 2) - d(3, 2, 1, 0) * d(3, 2, 1, 0));

    const mub = (d(0, 2, 3, 2) + mua * d(3, 2, 1, 0)) / d(3, 2, 3, 2);

    const P01 = P1.clone().sub(P0);
    const P23 = P3.clone().sub(P2);

    const Pa = P0.clone().add(P01.multiplyScalar(mua));
    const Pb = P2.clone().add(P23.multiplyScalar(mub));

    const center = Pa.clone().add(Pb).multiplyScalar(0.5);

    return center;
  }

  static computeCircleCenter(A: THREE.Vector3, B: THREE.Vector3, C: THREE.Vector3): THREE.Vector3 {
    const AB = B.clone().sub(A);
    const AC = C.clone().sub(A);

    const N = AC.clone().cross(AB).normalize();

    const ab_dir = AB.clone().cross(N).normalize();
    const ac_dir = AC.clone().cross(N).normalize();

    const ab_origin = A.clone().add(B).multiplyScalar(0.5);
    const ac_origin = A.clone().add(C).multiplyScalar(0.5);

    const P0 = ab_origin;
    const P1 = ab_origin.clone().add(ab_dir);

    const P2 = ac_origin;
    const P3 = ac_origin.clone().add(ac_dir);

    return Utils.lineToLineIntersection(P0, P1, P2, P3);
  }

  static getNorthVec(
    p1: THREE.Vector3,
    distance: number,
    projection: string | null,
  ): THREE.Vector3 {
    if (projection) {
      proj4.defs('pointcloud', projection);
      const transform = proj4('pointcloud', 'WGS84');

      const llP1 = transform.forward(p1.toArray());
      let llP2 = transform.forward([p1.x, p1.y + distance]);
      const polarRadius = Math.sqrt((llP2[0] - llP1[0]) ** 2 + (llP2[1] - llP1[1]) ** 2);
      llP2 = [llP1[0], llP1[1] + polarRadius];

      const northVec = transform.inverse(llP2);

      return new THREE.Vector3(...northVec, p1.z).sub(p1);
    } else {
      return new THREE.Vector3(0, 1, 0).multiplyScalar(distance);
    }
  }

  static computeAzimuth(p1: THREE.Vector3, p2: THREE.Vector3, projection: string | null): number {
    let azimuth = 0;

    if (projection) {
      let transform;

      if (projection.includes('EPSG')) {
        transform = proj4(projection, 'WGS84');
      } else {
        proj4.defs('pointcloud', projection);
        transform = proj4('pointcloud', 'WGS84');
      }

      const llP1 = transform.forward(p1.toArray());
      const llP2 = transform.forward(p2.toArray());
      const dir = [llP2[0] - llP1[0], llP2[1] - llP1[1]];
      azimuth = Math.atan2(dir[1], dir[0]) - Math.PI / 2;
    } else {
      const dir = [p2.x - p1.x, p2.y - p1.y];
      azimuth = Math.atan2(dir[1], dir[0]) - Math.PI / 2;
    }

    // make clockwise
    azimuth = -azimuth;

    return azimuth;
  }

  static screenPass: {
    screenScene: THREE.Scene;
    screenQuad: THREE.Mesh;
    camera: THREE.Camera;
    render: (renderer: THREE.WebGLRenderer, material: THREE.Material, target?: any) => void;
  };
}

Utils.screenPass = (() => {
  const screenScene = new THREE.Scene();
  const screenQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2, 1));
  (
    screenQuad.material as THREE.Material & {
      depthTest: boolean;
      depthWrite: boolean;
      transparent: boolean;
    }
  ).depthTest = true;
  (
    screenQuad.material as THREE.Material & {
      depthTest: boolean;
      depthWrite: boolean;
      transparent: boolean;
    }
  ).depthWrite = true;
  (
    screenQuad.material as THREE.Material & {
      depthTest: boolean;
      depthWrite: boolean;
      transparent: boolean;
    }
  ).transparent = true;
  screenScene.add(screenQuad);
  const camera = new THREE.Camera();

  return {
    screenScene,
    screenQuad,
    camera,
    render(renderer: THREE.WebGLRenderer, material: THREE.Material, target?: any) {
      screenQuad.material = material;
      if (typeof target === 'undefined') {
        renderer.render(screenScene, camera);
      } else {
        renderer.setRenderTarget(target);
        renderer.render(screenScene, camera);
        renderer.setRenderTarget(null);
      }
    },
  };
})();
